import { collection, query, where, getAggregateFromServer, count, doc, getDoc, sum, getDocs, orderBy, limit, startAfter, QueryDocumentSnapshot, DocumentData, getCountFromServer, updateDoc, documentId } from 'firebase/firestore';
import { db } from './firebase';
import { MonthlySnapshot, Customer, RushdMembershipStatus } from '../types';

// Pagination result type
export interface PaginatedCustomersResult {
  customers: Customer[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  total?: number;
}

// Search result type
export interface SearchCustomersResult {
  customers: Customer[];
  total: number;
}

const parseFirestoreDate = (timestamp: any): Date | null => {
  if (!timestamp) {
    return null;
  }

  if (timestamp instanceof Date) {
    return isNaN(timestamp.getTime()) ? null : timestamp;
  }

  if (timestamp && typeof timestamp === 'object' && typeof timestamp.toDate === 'function') {
    const date = timestamp.toDate();
    return date instanceof Date && !isNaN(date.getTime()) ? date : null;
  }

  if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
};

const rushdMembershipCache = new Map<string, RushdMembershipStatus | null>();
const RUSHD_MEMBERSHIP_BATCH_SIZE = 10;

const mapRushdMembershipData = (data: DocumentData): RushdMembershipStatus => {
  const currentPeriodEnd = parseFirestoreDate(data.currentPeriodEnd);
  const nextBillingDate = parseFirestoreDate(data.nextBillingDate);
  const status = data.status as RushdMembershipStatus['status'];
  const isActive = status === 'active' && !!(currentPeriodEnd && currentPeriodEnd > new Date());
  const usageThisPeriod = typeof data.usageThisPeriod === 'number' ? data.usageThisPeriod : undefined;
  const usageLimit = typeof data.usageLimit === 'number' ? data.usageLimit : undefined;

  return {
    isMember: true,
    isActive,
    status,
    plan: data.plan,
    currentPeriodEnd: currentPeriodEnd || undefined,
    nextBillingDate: nextBillingDate || undefined,
    cancelAtPeriodEnd: data.cancelAtPeriodEnd || false,
    usageThisPeriod,
    usageLimit,
  };
};

export const fetchRushdMembershipsForUserIds = async (userIds: string[]): Promise<Map<string, RushdMembershipStatus | null>> => {
  const uniqueIds = Array.from(new Set(userIds.filter(Boolean)));
  const idsToFetch = uniqueIds.filter(id => !rushdMembershipCache.has(id));
  const membershipCollection = collection(db, 'rush_d_memberships');

  for (let i = 0; i < idsToFetch.length; i += RUSHD_MEMBERSHIP_BATCH_SIZE) {
    const chunk = idsToFetch.slice(i, i + RUSHD_MEMBERSHIP_BATCH_SIZE);
    if (chunk.length === 0) continue;

    try {
      const membershipQuery = query(membershipCollection, where(documentId(), 'in', chunk));
      const snapshot = await getDocs(membershipQuery);
      const foundIds = new Set<string>();

      snapshot.forEach(docSnap => {
        const membership = mapRushdMembershipData(docSnap.data());
        rushdMembershipCache.set(docSnap.id, membership);
        foundIds.add(docSnap.id);
      });

      chunk.forEach(id => {
        if (!foundIds.has(id)) {
          rushdMembershipCache.set(id, null);
        }
      });
    } catch (error) {
      console.error('Error fetching Rush\'d membership chunk:', error);
      chunk.forEach(id => {
        if (!rushdMembershipCache.has(id)) {
          rushdMembershipCache.set(id, null);
        }
      });
    }
  }

  const result = new Map<string, RushdMembershipStatus | null>();
  uniqueIds.forEach(id => {
    result.set(id, rushdMembershipCache.get(id) ?? null);
  });
  return result;
};

export const attachRushdMemberships = async (customers: Customer[]): Promise<Customer[]> => {
  if (customers.length === 0) {
    return customers;
  }

  const membershipMap = await fetchRushdMembershipsForUserIds(customers.map(customer => customer.id));

  customers.forEach(customer => {
    const membership = membershipMap.get(customer.id);
    if (membership) {
      customer.rushdMembership = membership;
    } else {
      customer.rushdMembership = undefined;
    }
  });

  return customers;
};

/**
 * Helper function to process customer data consistently
 */
const processCustomerData = (doc: QueryDocumentSnapshot<DocumentData>): Customer => {
  const data = doc.data();

  const createdAt = parseFirestoreDate(data.createdAt) || new Date();
  const lastOrderDate = parseFirestoreDate(data.lastOrderDate) || undefined;
  const lastAccessDevice = data.lastAccessDevice ? {
    ...data.lastAccessDevice,
    recordedAt: parseFirestoreDate(data.lastAccessDevice.recordedAt) || undefined
  } : undefined;
  const notificationTokens = Array.isArray(data.notificationTokens) ? data.notificationTokens : [];

  return {
    id: doc.id,
    name: data.name || '',
    email: data.email || '',
    phone: data.phone || '',
    addresses: data.addresses || [],
    address: data.address || null,
    orderHistory: data.orderHistory || [],
    orderCount: data.orderCount,
    totalSpent: data.totalSpent || 0,
    averageOrderValue: data.averageOrderValue,
    lastOrderDate,
    notificationPreferences: data.notificationPreferences || {
      orderUpdates: true,
      promotions: false,
      newsletters: false
    },
    isActive: data.isActive,
    createdAt,
    lastAccessDevice,
    accessDeviceOrigins: data.accessDeviceOrigins,
    notificationTokens,
  } as Customer;
};

/**
 * Get the total count of customers
 */
export const getCustomerCount = async (searchQuery?: string): Promise<number> => {
  try {
    console.log('getCustomerCount called with:', { searchQuery });
    
    const customersCollection = collection(db, 'users');
    const countQuery = query(customersCollection);
    
    const snapshot = await getCountFromServer(countQuery);
    const count = snapshot.data().count;
    console.log('Customer count result:', count);
    
    return count;
  } catch (error) {
    console.error('Error getting customer count:', error);
    throw new Error('Failed to get customer count');
  }
};

/**
 * Fetch customers with pagination support
 */
export const fetchCustomersPaginated = async (
  pageSize: number = 20,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  searchQuery?: string
): Promise<PaginatedCustomersResult> => {
  try {
    console.log('fetchCustomersPaginated called with:', { pageSize, lastDoc: !!lastDoc, searchQuery });
    
    const customersCollection = collection(db, 'users');
    
    // Build base query - use __name__ (document ID) for consistent ordering
    let customerQuery = query(
      customersCollection,
      orderBy('__name__')
    );
    
    // Add pagination
    if (lastDoc) {
      customerQuery = query(customerQuery, startAfter(lastDoc));
    }
    
    customerQuery = query(customerQuery, limit(pageSize));
    
    console.log('Executing Firestore query...');
    const snapshot = await getDocs(customerQuery);
    console.log('Firestore query result:', { docsCount: snapshot.docs.length, empty: snapshot.empty });
    
    const customersWithMembership = await attachRushdMemberships(snapshot.docs.map(processCustomerData));
    console.log('Processed customers:', customersWithMembership.length);
    
    const hasMore = snapshot.docs.length === pageSize;
    const lastDocument = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    
    return {
      customers: customersWithMembership,
      lastDoc: lastDocument,
      hasMore,
    };
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw new Error('Failed to fetch customers');
  }
};

/**
 * Search customers by name, email, phone, or user ID
 */
export const searchCustomers = async (
  searchQuery: string
): Promise<SearchCustomersResult> => {
  try {
    const customersCollection = collection(db, 'users');
    const customerQuery = query(customersCollection, orderBy('name'));
    
    const snapshot = await getDocs(customerQuery);
    const allCustomers = await attachRushdMemberships(snapshot.docs.map(processCustomerData));
    
    // Filter by search query
    const searchLower = searchQuery.toLowerCase();
    const filteredCustomers = allCustomers.filter(customer => {
      // User ID exact/contains
      if (customer.id?.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Search by name and email
      if (customer.name.toLowerCase().includes(searchLower) ||
          customer.email.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search by phone number in customer.phone field
      if (customer.phone && customer.phone.includes(searchQuery)) {
        return true;
      }
      
      // Search by phone number in primary address
      if (customer.addresses && customer.addresses.length > 0) {
        const primaryAddress = customer.addresses.find(addr => addr.isDefault);
        if (primaryAddress && primaryAddress.phoneNumber && 
            primaryAddress.phoneNumber.includes(searchQuery)) {
          return true;
        }
      }
      
      return false;
    });
    
    return {
      customers: filteredCustomers,
      total: filteredCustomers.length
    };
  } catch (error) {
    console.error('Error searching customers:', error);
    throw new Error('Failed to search customers');
  }
};

/**
 * Fetch a single customer by ID
 */
export const fetchCustomerById = async (customerId: string): Promise<Customer | null> => {
  try {
    const customerDoc = doc(db, 'users', customerId);
    const snapshot = await getDoc(customerDoc);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    const [customer] = await attachRushdMemberships([processCustomerData(snapshot)]);
    return customer || null;
  } catch (error) {
    console.error('Error fetching customer:', error);
    throw new Error('Failed to fetch customer');
  }
};

/**
 * Update customer information
 */
export const updateCustomer = async (customerId: string, customerData: Partial<Customer>): Promise<void> => {
  try {
    console.log('Updating customer:', customerId, customerData);
    
    // Prepare the data for Firestore update
    // Only include fields that should be updated and handle Firestore data types
    const updateData: any = {};
    
    if (customerData.name !== undefined) {
      updateData.name = customerData.name;
    }
    
    if (customerData.isActive !== undefined) {
      updateData.isActive = customerData.isActive;
    }
    
    if (customerData.addresses !== undefined) {
      // Process addresses to ensure proper IDs for new addresses
      const processedAddresses = customerData.addresses.map(address => {
        // Generate a proper ID only for addresses with temporary IDs
        if (address.id && address.id.startsWith('temp_')) {
          return {
            ...address,
            id: `addr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
          };
        }
        return address;
      });
      
      updateData.addresses = processedAddresses;
    }
    
    if (customerData.notificationPreferences !== undefined) {
      updateData.notificationPreferences = customerData.notificationPreferences;
    }
    
    // Add updatedAt timestamp
    updateData.updatedAt = new Date();
    
    const customerDoc = doc(db, 'users', customerId);
    await updateDoc(customerDoc, updateData);
    
    console.log('Customer updated successfully in Firestore');
  } catch (error) {
    console.error('Error updating customer:', error);
    throw new Error('Failed to update customer');
  }
};

/**
 * Get active customer count using aggregation
 */
export const getActiveCustomerCount = async (): Promise<number> => {
  try {
    console.log('Getting active customer count with aggregation...');
    
    const customersCollection = collection(db, 'users');
    const snapshot = await getAggregateFromServer(
      query(
        customersCollection,
        where('isActive', '==', true)
      ),
      { activeCustomerCount: count() }
    );
    
    const activeCount = snapshot.data().activeCustomerCount;
    console.log('Active customers (aggregation):', activeCount);
    
    return activeCount;
  } catch (error) {
    console.error('Error getting active customer count:', error);
    throw error;
  }
};

/**
 * Get total customer count using aggregation
 */
export const getTotalCustomerCount = async (): Promise<number> => {
  try {
    console.log('Getting total customer count with aggregation...');
    
    const customersCollection = collection(db, 'users');
    const snapshot = await getAggregateFromServer(
      customersCollection,
      { totalCustomerCount: count() }
    );
    
    const totalCount = snapshot.data().totalCustomerCount;
    console.log('Total customers (aggregation):', totalCount);
    
    return totalCount;
  } catch (error) {
    console.error('Error getting total customer count:', error);
    throw error;
  }
};

/**
 * Get new customers count for current month using aggregation
 */
export const getNewCustomersThisMonth = async (): Promise<number> => {
  try {
    console.log('Getting new customers this month with aggregation...');
    
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    
    console.log(`Date range: ${startOfMonth.toISOString()} to ${endOfDay.toISOString()}`);
    
    const customersCollection = collection(db, 'users');
    const snapshot = await getAggregateFromServer(
      query(
        customersCollection,
        where('createdAt', '>=', startOfMonth),
        where('createdAt', '<=', endOfDay)
      ),
      { newCustomersCount: count() }
    );
    
    const newCount = snapshot.data().newCustomersCount;
    console.log('New customers this month (aggregation):', newCount);
    
    return newCount;
  } catch (error) {
    console.error('Error getting new customers this month:', error);
    throw error;
  }
};

/**
 * Get new customers count for same period in previous month
 */
export const getNewCustomersPreviousMonthSamePeriod = async (): Promise<number> => {
  try {
    const now = new Date();
    const currentDay = now.getDate();
    
    // Calculate previous month dates
    const prevMonth = now.getMonth() - 1;
    const prevYear = prevMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
    const adjustedPrevMonth = prevMonth < 0 ? 11 : prevMonth;
    
    // Get the number of days in the previous month
    const daysInPrevMonth = new Date(prevYear, adjustedPrevMonth + 1, 0).getDate();
    
    // Use the minimum of current day or days available in previous month
    const comparisonDay = Math.min(currentDay, daysInPrevMonth);
    
    const prevMonthStart = new Date(prevYear, adjustedPrevMonth, 1);
    const prevMonthEnd = new Date(prevYear, adjustedPrevMonth, comparisonDay, 23, 59, 59, 999);
    
    console.log(`Previous month same period: ${prevMonthStart.toISOString()} to ${prevMonthEnd.toISOString()}`);
    
    const customersCollection = collection(db, 'users');
    const snapshot = await getAggregateFromServer(
      query(
        customersCollection,
        where('createdAt', '>=', prevMonthStart),
        where('createdAt', '<=', prevMonthEnd)
      ),
      { newCustomersCount: count() }
    );
    
    const newCount = snapshot.data().newCustomersCount;
    console.log('New customers previous month same period (aggregation):', newCount);
    
    return newCount;
  } catch (error) {
    console.error('Error getting new customers previous month same period:', error);
    return 0;
  }
};

/**
 * Get a monthly snapshot by year and month
 */
export const getMonthlySnapshot = async (year: number, month: number): Promise<MonthlySnapshot | null> => {
  try {
    const snapshotId = `${year}-${month.toString().padStart(2, '0')}`;
    console.log('Getting monthly snapshot:', snapshotId);
    
    const snapshotDoc = doc(db, 'monthly_snapshots', snapshotId);
    const snapshot = await getDoc(snapshotDoc);
    
    if (!snapshot.exists()) {
      console.log('Monthly snapshot not found:', snapshotId);
      return null;
    }
    
    const data = snapshot.data();
    return {
      snapshotId: data.snapshotId,
      year: data.year,
      month: data.month,
      totalProducts: data.totalProducts,
      lowStockProducts: data.lowStockProducts,
      outOfStockProducts: data.outOfStockProducts,
      totalInventoryValue: data.totalInventoryValue,
      totalOrders: data.totalOrders || 0,
      totalRevenue: data.totalRevenue || 0,
      totalCustomers: data.totalCustomers || 0,
      createdAt: data.createdAt.toDate ? data.createdAt.toDate() : data.createdAt,
    };
  } catch (error) {
    console.error('Error getting monthly snapshot:', error);
    return null;
  }
};

/**
 * Get the previous month's snapshot for comparison
 */
export const getPreviousMonthSnapshot = async (): Promise<MonthlySnapshot | null> => {
  try {
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth(); // Current month (0-indexed)
    
    // Go to previous month
    if (month === 0) {
      month = 12;
      year -= 1;
    }
    
    return await getMonthlySnapshot(year, month);
  } catch (error) {
    console.error('Error getting previous month snapshot:', error);
    return null;
  }
};

/**
 * Calculate percentage change between current and previous values
 */
export const calculatePercentageChange = (current: number, previous: number): number => {
  if (previous === 0) {
    return current > 0 ? 100 : 0; // If previous was 0 and current > 0, show 100% increase
  }
  return ((current - previous) / previous) * 100;
};

/**
 * Get customer statistics with percentage changes from previous month
 */
export const getCustomerStatisticsWithComparison = async (): Promise<{
  current: {
    totalCustomers: number;
    activeCustomers: number;
    newCustomersThisMonth: number;
    averageLTV: number;
  };
  previous: MonthlySnapshot | null;
  percentageChanges: {
    totalCustomers: number;
    activeCustomers: number;
    newCustomersThisMonth: number;
    averageLTV: number;
  };
}> => {
  try {
    console.log('Getting customer statistics with same-period comparison...');
    
    // Get current statistics and previous same-period statistics in parallel
    const [
      totalCustomers,
      activeCustomers,
      currentNewCustomers,
      previousNewCustomers,
      currentAverageLTV,
      previousAverageLTV
    ] = await Promise.all([
      getTotalCustomerCount(),
      getActiveCustomerCount(),
      getNewCustomersThisMonth(),
      getNewCustomersPreviousMonthSamePeriod(),
      getAverageCustomerLTV(),
      getAverageLTVPreviousMonthSamePeriod()
    ]);
    
    const current = {
      totalCustomers,
      activeCustomers,
      newCustomersThisMonth: currentNewCustomers,
      averageLTV: currentAverageLTV
    };
    
    // Get previous month's snapshot for total/active customers comparison (still using snapshots for these)
    const previous = await getPreviousMonthSnapshot();
    
    // Calculate percentage changes using same-period comparison for new customers and LTV
    const percentageChanges = {
      totalCustomers: previous ? calculatePercentageChange(current.totalCustomers, previous.totalCustomers) : 0,
      activeCustomers: previous && previous.totalCustomers > 0 ? calculatePercentageChange(current.activeCustomers, previous.totalCustomers) : 0,
      newCustomersThisMonth: calculatePercentageChange(currentNewCustomers, previousNewCustomers),
      averageLTV: calculatePercentageChange(currentAverageLTV, previousAverageLTV)
    };
    
    console.log('Customer statistics same-period comparison:', { 
      current, 
      previous: { 
        totalCustomers: previous?.totalCustomers, 
        newCustomersPrevSamePeriod: previousNewCustomers,
        averageLTVPrevSamePeriod: previousAverageLTV
      }, 
      percentageChanges 
    });
    
    return {
      current,
      previous,
      percentageChanges
    };
  } catch (error) {
    console.error('Error getting customer statistics with same-period comparison:', error);
    throw error;
  }
};

/**
 * Get average customer LTV using aggregation
 */
export const getAverageCustomerLTV = async (): Promise<number> => {
  try {
    console.log('Getting average customer LTV with aggregation...');
    
    const customersCollection = collection(db, 'users');
    const snapshot = await getAggregateFromServer(
      query(
        customersCollection,
        where('orderCount', '>', 0), // Only customers who have made orders
        where('calculatedLTV', '>', 0) // Only customers with calculated LTV
      ),
      { 
        totalLTV: sum('calculatedLTV'),
        customerCount: count()
      }
    );
    
    const data = snapshot.data();
    const totalLTV = data.totalLTV || 0;
    const customerCount = data.customerCount || 0;
    
    const averageLTV = customerCount > 0 ? totalLTV / customerCount : 0;
    console.log('Average customer LTV (aggregation):', averageLTV);
    
    return averageLTV;
  } catch (error) {
    console.error('Error getting average customer LTV:', error);
    throw error;
  }
};

/**
 * Get total customer LTV using aggregation
 */
export const getTotalCustomerLTV = async (): Promise<number> => {
  try {
    console.log('Getting total customer LTV with aggregation...');
    
    const customersCollection = collection(db, 'users');
    const snapshot = await getAggregateFromServer(
      query(
        customersCollection,
        where('orderCount', '>', 0),
        where('calculatedLTV', '>', 0)
      ),
      { totalLTV: sum('calculatedLTV') }
    );
    
    const totalLTV = snapshot.data().totalLTV || 0;
    console.log('Total customer LTV (aggregation):', totalLTV);
    
    return totalLTV;
  } catch (error) {
    console.error('Error getting total customer LTV:', error);
    throw error;
  }
};

/**
 * Get LTV data from the most recent calculation
 */
export const getCurrentLTVCalculation = async (): Promise<{
  averageLTV: number;
  totalLTV: number;
  totalCustomers: number;
  calculatedAt: Date | null;
} | null> => {
  try {
    console.log('Getting current LTV calculation...');
    
    const ltvDoc = doc(db, 'ltv_calculations', 'current');
    const snapshot = await getDoc(ltvDoc);
    
    if (!snapshot.exists()) {
      console.log('No LTV calculation found');
      return null;
    }
    
    const data = snapshot.data();
    return {
      averageLTV: data.averageLTV || 0,
      totalLTV: data.totalLTV || 0,
      totalCustomers: data.totalCustomers || 0,
      calculatedAt: data.calculatedAt?.toDate() || null
    };
  } catch (error) {
    console.error('Error getting current LTV calculation:', error);
    return null;
  }
};

/**
 * Get average LTV for same period in previous month
 * Uses the LTV values that were calculated at that time
 */
export const getAverageLTVPreviousMonthSamePeriod = async (): Promise<number> => {
  try {
    console.log('Getting average LTV for previous month same period...');
    
    // For now, we'll use a simplified approach and calculate based on historical customer data
    // In a production environment, you'd want to store historical LTV snapshots
    
    const now = new Date();
    const currentDay = now.getDate();
    
    // Calculate previous month dates
    const prevMonth = now.getMonth() - 1;
    const prevYear = prevMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
    const adjustedPrevMonth = prevMonth < 0 ? 11 : prevMonth;
    
    // Get the number of days in the previous month
    const daysInPrevMonth = new Date(prevYear, adjustedPrevMonth + 1, 0).getDate();
    
    // Use the minimum of current day or days available in previous month
    const comparisonDay = Math.min(currentDay, daysInPrevMonth);
    
    const prevMonthStart = new Date(prevYear, adjustedPrevMonth, 1);
    const prevMonthEnd = new Date(prevYear, adjustedPrevMonth, comparisonDay, 23, 59, 59, 999);
    
    console.log(`Previous month same period for LTV: ${prevMonthStart.toISOString()} to ${prevMonthEnd.toISOString()}`);
    
    // Get customers who were active during that period
    const customersCollection = collection(db, 'users');
    const snapshot = await getAggregateFromServer(
      query(
        customersCollection,
        where('orderCount', '>', 0),
        where('calculatedLTV', '>', 0),
        where('lastOrderDate', '>=', prevMonthStart),
        where('lastOrderDate', '<=', prevMonthEnd)
      ),
      { 
        totalLTV: sum('calculatedLTV'),
        customerCount: count()
      }
    );
    
    const data = snapshot.data();
    const totalLTV = data.totalLTV || 0;
    const customerCount = data.customerCount || 0;
    
    const averageLTV = customerCount > 0 ? totalLTV / customerCount : 0;
    console.log('Average LTV previous month same period (aggregation):', averageLTV);
    
    return averageLTV;
  } catch (error) {
    console.error('Error getting average LTV previous month same period:', error);
    return 0;
  }
}; 

/**
 * Send message to customer via backend email service
 */
export interface CustomerMessageData {
  customerId: string;
  customerEmail: string;
  customerName: string;
  subject: string;
  content: string;
  template: string;
  category: string;
  plainTextOnly?: boolean;
}

export const sendCustomerMessage = async (messageData: CustomerMessageData): Promise<void> => {
  try {
    console.log('Sending customer message:', messageData);
    
    // Import functions after Firebase is initialized
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions();
    
    // Call the backend function to send the message
    const sendCustomerMessageFunction = httpsCallable(functions, 'sendCustomerMessage');
    
    const result = await sendCustomerMessageFunction({
      customerId: messageData.customerId,
      customerEmail: messageData.customerEmail,
      customerName: messageData.customerName,
      subject: messageData.subject,
      content: messageData.content,
      template: messageData.template,
      category: messageData.category,
      plainTextOnly: messageData.plainTextOnly || false,
      sentAt: new Date().toISOString(),
      sentBy: 'admin' // For now, we'll use a static admin identifier
    });
    
    console.log('Customer message sent successfully:', result);
  } catch (error) {
    console.error('Error sending customer message:', error);
    throw new Error('Failed to send message to customer');
  }
}; 
